import { ExecutionPanel } from "../components/execution-panel";
import { useCallback, useEffect, useState, type CSSProperties } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  APPOINTMENT_STATUS_LABELS,
  APPOINTMENT_STATUS_ORDER,
  APPOINTMENT_STEP_ACTIONS,
  BOOKING_CHANNEL_LABELS,
  CLIENT_TYPE_LABELS,
  LEAD_SOURCE_LABELS,
  formatCad,
  nextAppointmentStatus,
  type AppointmentStatus,
  type AppointmentView,
  type CategoryWithServices,
  type ClientDetail,
  type DiscountRule,
  lineTotalCents,
} from "@shared/modules/x-atelier";
import {
  CALL_REQUEST_KIND_LABELS,
  REQUEST_SMS_STATUS_LABELS,
  type CallRequest,
} from "@shared/modules/x-appels";
import { ApiError } from "../../../core/lib/api";
import { moduleEnabled } from "../../../core/blueprint";
import { formatDateTime, toDate } from "../../../core/lib/format";
import { useAuthStore } from "../../../core/stores/auth";
import { useToastStore } from "../../../core/stores/toast";
import { Button } from "../../../core/components/ui/button";
import { Icon } from "../../../core/components/ui/icon";
import { Select, Textarea } from "../../../core/components/ui/input";
import { Modal } from "../../../core/components/ui/modal";
import { Badge } from "../../../core/components/ui/badge";
import { BackLink } from "../../../core/components/ui/back-link";
import { useGoBack } from "../../../core/lib/navigation";
import { AppointmentModal } from "../components/appointment-modal";
import {
  AppointmentJournal,
  BookingChannelBadge,
  NewClientBadge,
  PresenceBadge,
} from "../components/booking-badges";
import { VehicleCorrectionHint } from "../components/vehicle-identity";
import {
  BundleHint,
  LinesEditor,
  applyBundle,
  bundleSuggestions,
  draftFromLine,
  linesToInput,
  withDiscountRules,
  type DraftLine,
} from "../components/lines-editor";
import { PaymentsPanel } from "../components/payments-panel";
import { QuickActions } from "../components/quick-actions";
import { StatusBadge } from "../components/status-badge";
import { StatusStepper } from "../components/status-stepper";
import { ServiceGuidesList, guidesEnabled } from "../components/service-guides";
import {
  deleteAppointment,
  getAppointment,
  getCatalogue,
  getClient,
  listAppointmentRequests,
  listDiscountRules,
  markRequestHandled,
  setAppointmentStatus,
  updateAppointment,
} from "../lib/api";
import { formatDayLong, formatDuration, formatTimeRange } from "../lib/dates";
import { isInactive } from "../lib/status";

type Failure = "not_found" | "error" | null;

/**
 * Fiche d'un travail, pensée pour la tablette du technicien : le véhicule et
 * le client en tête avec les gestes pour le joindre, la frise des étapes et
 * UN gros bouton pour passer à la suivante, puis les services, les montants,
 * les procédures et les notes.
 */
export function TravailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  // « Retour » ramène à la page d'où l'on vient (agenda, liste filtrée, fiche
  // client…) ; ouverte directement, la fiche revient à la liste des travaux.
  const goBack = useGoBack("/travaux");
  const push = useToastStore((s) => s.push);
  const isAdmin = useAuthStore((s) => s.user?.role === "admin");

  const [a, setA] = useState<AppointmentView | null>(null);
  const [failure, setFailure] = useState<Failure>(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);

  // Fiche client (véhicule détaillé, adresse pour l'itinéraire).
  const [detail, setDetail] = useState<ClientDetail | null>(null);

  // Lignes en édition sur place.
  const [editingLines, setEditingLines] = useState(false);
  const [lines, setLines] = useState<DraftLine[]>([]);
  // Services au rendez-vous quand l'édition commence : en tête du sélecteur.
  const [plannedIds, setPlannedIds] = useState<string[]>([]);
  const [catalogue, setCatalogue] = useState<CategoryWithServices[]>([]);
  const [rules, setRules] = useState<DiscountRule[]>([]);

  // Notes en édition sur place.
  const [editingNotes, setEditingNotes] = useState(false);
  const [notes, setNotes] = useState("");
  // Demandes prises par Léo sur ce rendez-vous (annulation, rappel), à traiter.
  const [requests, setRequests] = useState<CallRequest[]>([]);

  const load = useCallback(() => {
    if (!id) return;
    listAppointmentRequests(id)
      .then(setRequests)
      .catch(() => setRequests([]));
    getAppointment(id)
      .then((view) => {
        setA(view);
        setFailure(null);
      })
      .catch((err: unknown) => {
        setA(null);
        setFailure(err instanceof ApiError && err.status === 404 ? "not_found" : "error");
      });
  }, [id]);

  useEffect(load, [load]);

  useEffect(() => {
    if (!a?.clientId) return;
    getClient(a.clientId)
      .then(setDetail)
      .catch(() => setDetail(null));
  }, [a?.clientId]);

  // Le catalogue sert aux procédures liées (guides des services des lignes) et à l'éditeur de lignes.
  useEffect(() => {
    getCatalogue()
      .then(setCatalogue)
      .catch(() => setCatalogue([]));
    listDiscountRules()
      .then(setRules)
      .catch(() => setRules([]));
  }, []);

  const changeStatus = async (status: AppointmentStatus) => {
    if (!id || busy) return;
    if (a?.schedulePending && !["devis", "annule"].includes(status)) {
      setEditing(true);
      push("info", "Choisissez une date pour planifier ce devis.");
      return;
    }
    setBusy(true);
    try {
      setA(await setAppointmentStatus(id, status));
      push("success", `Statut : ${APPOINTMENT_STATUS_LABELS[status]}`);
    } catch {
      push("danger", "Changement de statut impossible");
    } finally {
      setBusy(false);
    }
  };

  const startLines = () => {
    if (!a) return;
    setLines(a.lines.map(draftFromLine));
    setPlannedIds(a.lines.flatMap((l) => (l.serviceId ? [l.serviceId] : [])));
    setEditingLines(true);
    if (catalogue.length === 0) {
      getCatalogue()
        .then(setCatalogue)
        .catch(() => setCatalogue([]));
    }
  };

  const saveLines = async () => {
    if (!id || busy) return;
    const input = linesToInput(lines);
    if (!input) {
      push(
        "danger",
        "Lignes incomplètes",
        "Chaque ligne doit avoir un libellé et un prix lisible.",
      );
      return;
    }
    setBusy(true);
    try {
      setA(await updateAppointment(id, { lines: input }));
      setEditingLines(false);
      push("success", "Lignes enregistrées");
    } catch {
      push("danger", "Enregistrement impossible");
    } finally {
      setBusy(false);
    }
  };

  const saveNotes = async () => {
    if (!id || busy) return;
    setBusy(true);
    try {
      setA(await updateAppointment(id, { notes: notes.trim() || null }));
      setEditingNotes(false);
      push("success", "Notes enregistrées");
    } catch {
      push("danger", "Enregistrement impossible");
    } finally {
      setBusy(false);
    }
  };

  // Proposition de forfait en lecture : on remplace et on enregistre d'un coup.
  const services = catalogue.flatMap((c) => c.services);
  const readSuggestion =
    a && !editingLines ? bundleSuggestions(a.lines.map(draftFromLine), services)[0] : undefined;
  const applySuggestion = async () => {
    if (!id || !a || !readSuggestion || busy) return;
    const next = withDiscountRules(applyBundle(a.lines.map(draftFromLine), readSuggestion), rules);
    const input = linesToInput(next);
    if (!input) return;
    setBusy(true);
    try {
      setA(await updateAppointment(id, { lines: input }));
      push(
        "success",
        "Forfait appliqué",
        `${readSuggestion.bundle.name} remplace les services séparés.`,
      );
    } catch {
      push("danger", "Enregistrement impossible");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!id || busy) return;
    setBusy(true);
    try {
      await deleteAppointment(id);
      push("success", "Rendez-vous supprimé");
      goBack();
    } catch {
      push("danger", "Suppression impossible");
      setBusy(false);
    }
  };

  if (failure) {
    return (
      <div className="page">
        <p className="atelier-backlink">
          <BackLink fallback="/travaux" />
        </p>
        <p className="empty">
          {failure === "not_found"
            ? "Travail introuvable — il a peut-être été supprimé."
            : "Fiche indisponible — réessayez dans un instant."}
        </p>
      </div>
    );
  }

  if (!a) return <p className="empty">Chargement…</p>;

  const next = nextAppointmentStatus(a.status);
  const start = toDate(a.startAt);
  const off = isInactive(a.status);
  const client = detail?.client ?? null;
  const vehicle = detail?.vehicles.find((v) => v.id === a.vehicleId) ?? null;
  const address = client
    ? [client.address, client.city, client.postalCode].filter(Boolean).join(", ")
    : "";
  const vehicleFacts = vehicle
    ? [
        vehicle.plate,
        vehicle.color,
        vehicle.mileage !== null ? `${vehicle.mileage.toLocaleString("fr-CA")} km` : null,
        vehicle.vin ? `NIV ${vehicle.vin}` : null,
      ].filter(Boolean)
    : [];

  // Procédures : les guides liés aux services des lignes, sans doublon.
  const serviceById = new Map(catalogue.flatMap((c) => c.services.map((s) => [s.id, s] as const)));
  const guideSlugs = Array.from(
    new Set(
      a.lines.flatMap((l) => (l.serviceId ? (serviceById.get(l.serviceId)?.guideSlugs ?? []) : [])),
    ),
  );

  // Travail terminé : on peut émettre la garantie antirouille (module local x-garanties).
  const canWarranty =
    moduleEnabled("x-garanties") && ["termine", "pret", "recupere"].includes(a.status);

  const techStyle = a.technicianColor
    ? ({ "--tech-color": a.technicianColor } as CSSProperties)
    : undefined;

  return (
    <div className="page travail">
      <p className="atelier-backlink">
        <BackLink fallback="/travaux" />
      </p>

      {/* ---- En-tête : véhicule, horaire, client ---- */}
      <header className="travail__hero" style={techStyle}>
        <div className="travail__hero-main">
          <p className="travail__eyebrow">
            <StatusBadge status={a.status} />
            <BookingChannelBadge channel={a.bookingChannel} />
            <PresenceBadge at={a.presenceConfirmedAt} />
            <NewClientBadge status={a.clientStatus} />
            {a.invoiceNumber ? <Badge variant="success">Facture {a.invoiceNumber}</Badge> : null}
            {a.quoteNumber && !a.invoiceNumber ? (
              <Badge variant="neutral">Devis {a.quoteNumber}</Badge>
            ) : null}
          </p>
          <h1 className="travail__title">
            <Icon name="car" size={28} />
            {a.title}
          </h1>
          {vehicleFacts.length > 0 ? (
            <p className="travail__facts">{vehicleFacts.join(" · ")}</p>
          ) : null}
          {vehicle ? (
            <VehicleCorrectionHint
              vehicle={vehicle}
              onFixed={() => {
                load();
                if (a.clientId)
                  getClient(a.clientId)
                    .then(setDetail)
                    .catch(() => undefined);
              }}
            />
          ) : null}
          <p className="travail__when">
            <span className="travail__when-item">
              <Icon name="calendar" size={16} />
              {a.schedulePending ? "À planifier" : start ? formatDayLong(start) : "—"}
            </span>
            <span className="travail__when-item">
              <Icon name="clock" size={16} />
              {a.schedulePending ? "Horaire à fixer" : formatTimeRange(a.startAt, a.endAt)}
            </span>
            <span className="travail__when-item">
              <span className="travail__tech-dot" aria-hidden="true" />
              {a.technicianName ?? "Non attribué"}
            </span>
          </p>
        </div>
        <div className="travail__hero-client">
          <span className="travail__client-label">Client</span>
          <Link to={`/clients/${a.clientId}`} className="travail__client-name">
            {a.clientName}
          </Link>
          <span className="travail__client-meta">
            {client ? CLIENT_TYPE_LABELS[client.type] : null}
            {client && a.clientPhone ? " · " : null}
            {a.clientPhone}
          </span>
          <QuickActions phone={a.clientPhone} email={client?.email} address={address || null} />
        </div>
      </header>

      {requests
        .filter((r) => r.status === "a_traiter")
        .map((r) => (
          <div key={r.id} className="travail__notice travail__request" role="status">
            <Icon name="phone" size={20} />
            <span>
              <strong>Léo : {CALL_REQUEST_KIND_LABELS[r.kind].toLowerCase()}</strong>
              {r.callerName ? ` par ${r.callerName}` : ""} ({r.phone}) le{" "}
              {formatDateTime(r.createdAt, { dateStyle: "medium", timeStyle: "short" })}
              {r.reason ? ` · ${r.reason}` : ""}.{" "}
              {r.kind === "annulation"
                ? "Le rendez-vous reste à l’horaire tant que l’équipe n’a pas rappelé le client."
                : "Le client attend un rappel."}{" "}
              <em>{REQUEST_SMS_STATUS_LABELS[r.smsStatus]}.</em>
            </span>
            <Button
              variant="ghost"
              size="sm"
              disabled={busy}
              onClick={() =>
                void markRequestHandled(r.id)
                  .then((next) =>
                    setRequests((list) => list.map((x) => (x.id === next.id ? next : x))),
                  )
                  .catch(() => push("danger", "Mise à jour impossible"))
              }
            >
              Marquer traitée
            </Button>
          </div>
        ))}

      {/* ---- Étapes et action principale ---- */}
      {off ? (
        <div className="travail__notice" role="status">
          <Icon name="alert-circle" size={20} />
          <span>
            Ce travail est <strong>{APPOINTMENT_STATUS_LABELS[a.status].toLowerCase()}</strong>.
          </span>
          <Button
            variant="ghost"
            size="sm"
            disabled={busy}
            onClick={() => void changeStatus("reserve")}
          >
            Réactiver
          </Button>
        </div>
      ) : (
        <StatusStepper status={a.status} disabled={busy} onSelect={(s) => void changeStatus(s)} />
      )}

      <div className="travail__cta">
        {next ? (
          <Button
            variant="success"
            size="lg"
            className="travail__next"
            disabled={busy}
            icon={<Icon name={next === "en_cours" ? "play" : "check"} size={22} />}
            onClick={() => void changeStatus(next)}
          >
            {APPOINTMENT_STEP_ACTIONS[next]}
          </Button>
        ) : null}
        <div className="travail__actions">
          <Button
            variant="ghost"
            icon={<Icon name="edit" size={16} />}
            onClick={() => setEditing(true)}
          >
            Modifier
          </Button>
          {canWarranty ? (
            <Button
              variant="ghost"
              icon={<Icon name="shield" size={16} />}
              onClick={() => navigate(`/garanties/nouvelle?rdv=${a.id}`)}
            >
              Garantie
            </Button>
          ) : null}
          <Select
            aria-label="Autre statut"
            className="travail__status"
            value={a.status}
            disabled={busy}
            onChange={(e) => void changeStatus(e.target.value as AppointmentStatus)}
          >
            {APPOINTMENT_STATUS_ORDER.map((s) => (
              <option key={s} value={s}>
                {APPOINTMENT_STATUS_LABELS[s]}
              </option>
            ))}
          </Select>
          {isAdmin ? (
            <Button variant="ghost-danger" onClick={() => setConfirming(true)}>
              Supprimer
            </Button>
          ) : null}
        </div>
      </div>

      <div className="travail__grid">
        {/* ---- Services ---- */}
        <section className="card travail__block">
          <div className="travail__block-head">
            <h2 className="travail__block-title">Services</h2>
            {editingLines ? (
              <span className="travail__block-actions">
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={busy}
                  onClick={() => setEditingLines(false)}
                >
                  Annuler
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  disabled={busy}
                  onClick={() => void saveLines()}
                >
                  Enregistrer
                </Button>
              </span>
            ) : (
              <Button variant="ghost" size="sm" onClick={startLines}>
                Modifier
              </Button>
            )}
          </div>
          {editingLines ? (
            <LinesEditor
              vehicleClass={vehicle?.pricingClass}
              lines={lines}
              onChange={setLines}
              catalogue={catalogue}
              rules={rules}
              priorityIds={plannedIds}
            />
          ) : a.lines.length === 0 ? (
            <p className="travail__empty">Aucune ligne de service.</p>
          ) : (
            <ul className="service-lines">
              {readSuggestion ? (
                <li className="service-lines__hint">
                  <BundleHint
                    suggestion={readSuggestion}
                    disabled={busy}
                    onApply={() => void applySuggestion()}
                  />
                </li>
              ) : null}
              {a.lines.map((l) => (
                <li key={l.id} className="service-lines__item">
                  <span className="service-lines__main">
                    <span className="service-lines__label">{l.label}</span>
                    <span className="service-lines__meta">
                      {formatDuration(l.durationMin)}
                      {l.quantity > 1 ? ` · ${l.quantity} ×` : ""}
                      {l.discountPct > 0 ? (
                        <span className="service-lines__discount">
                          −{l.discountPct} %{l.discountLabel ? ` · ${l.discountLabel}` : ""}
                        </span>
                      ) : null}
                    </span>
                  </span>
                  <span className="service-lines__price">
                    {l.discountPct > 0 ? (
                      <s className="service-lines__was">{formatCad(l.priceCents * l.quantity)}</s>
                    ) : null}
                    {formatCad(lineTotalCents(l))}
                  </span>
                </li>
              ))}
              <li className="service-lines__total">
                <span>Sous-total</span>
                <span>{formatCad(a.totalCents)}</span>
              </li>
            </ul>
          )}
        </section>
        <section className="card travail__block">
          <ExecutionPanel appointmentId={a.id} />
        </section>

        <PaymentsPanel appointment={a} busy={busy} onChange={setA} />

        {/* ---- Procédures ---- */}
        {guidesEnabled() ? (
          <section className="card travail__block">
            <h2 className="travail__block-title">Procédures</h2>
            <ServiceGuidesList slugs={guideSlugs} />
          </section>
        ) : null}

        {/* ---- Notes ---- */}
        <section className="card travail__block">
          <div className="travail__block-head">
            <h2 className="travail__block-title">Notes</h2>
            {editingNotes ? (
              <span className="travail__block-actions">
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={busy}
                  onClick={() => setEditingNotes(false)}
                >
                  Annuler
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  disabled={busy}
                  onClick={() => void saveNotes()}
                >
                  Enregistrer
                </Button>
              </span>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setNotes(a.notes ?? "");
                  setEditingNotes(true);
                }}
              >
                Modifier
              </Button>
            )}
          </div>
          {editingNotes ? (
            <Textarea
              aria-label="Notes"
              rows={5}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="État du véhicule, consignes, demandes du client…"
              autoFocus
            />
          ) : a.notes ? (
            <p className="travail__notes">{a.notes}</p>
          ) : (
            <p className="travail__empty">Aucune note.</p>
          )}
          {vehicle?.notes ? (
            <p className="travail__vehicle-notes">
              <strong>Véhicule :</strong> {vehicle.notes}
            </p>
          ) : null}
          {a.clientInstructions ? (
            <p className="travail__instructions">
              <strong>Consignes pour le client :</strong> {a.clientInstructions}
            </p>
          ) : null}
        </section>

        {/* ---- Détails ---- */}
        <section className="card travail__block">
          <h2 className="travail__block-title">Détails</h2>
          <dl className="kv">
            <dt>Client</dt>
            <dd>
              <Link to={`/clients/${a.clientId}`}>{a.clientName}</Link>
            </dd>
            <dt>Véhicule</dt>
            <dd>{a.vehicleLabel ?? "Aucun"}</dd>
            <dt>Pris</dt>
            <dd>{BOOKING_CHANNEL_LABELS[a.bookingChannel] ?? "Au CRM"}</dd>
            <dt>Source</dt>
            <dd>{LEAD_SOURCE_LABELS[a.source]}</dd>
            <dt>Créé le</dt>
            <dd>{formatDateTime(a.createdAt, { dateStyle: "medium", timeStyle: "short" })}</dd>
            {a.invoicedAt ? (
              <>
                <dt>Facturé le</dt>
                <dd>{formatDateTime(a.invoicedAt, { dateStyle: "medium" })}</dd>
              </>
            ) : null}
          </dl>
        </section>
      </div>

      {/* ---- Journal ---- */}
      <section className="card travail__block travail__journal">
        <h2 className="travail__block-title">Historique</h2>
        <AppointmentJournal appointmentId={a.id} version={a.updatedAt} />
      </section>

      <AppointmentModal
        open={editing}
        appointment={a}
        onClose={() => setEditing(false)}
        onSaved={(saved) => {
          setEditing(false);
          setA(saved);
        }}
      />

      <Modal
        open={confirming}
        title="Supprimer ce rendez-vous ?"
        onClose={() => setConfirming(false)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirming(false)}>
              Annuler
            </Button>
            <Button variant="danger" disabled={busy} onClick={() => void remove()}>
              Supprimer définitivement
            </Button>
          </>
        }
      >
        <p>
          « {a.title} » et ses lignes de service seront supprimés. Le client et son véhicule
          restent.
        </p>
      </Modal>
    </div>
  );
}
